// Local teacher mode: custom lessons saved in localStorage so a teacher can
// build word lists without any backend running. Compatible with the seed
// lesson shape so the rest of the game (play list, lesson player, leaderboard
// etc.) treats them identically.
//
// Swap this file for an API-backed implementation when the server-side
// teacher dashboard is wired — the public surface (list / get / save /
// remove) stays the same.
import type { ThemeId, TimeOfDay, WordDTO } from "@shared/types";
import type { SeedLesson } from "../data/seed-lessons";

const STORAGE_KEY = "wr_custom_lessons_v1";
const TEACHER_AUTH_KEY = "wr_teacher_unlocked_v1";

export interface CustomLesson extends SeedLesson {
  /** True for any lesson built by the teacher (never for seed lessons). */
  isCustom: true;
  createdAt: number;
  updatedAt: number;
}

export function isTeacherUnlocked(): boolean {
  try {
    return localStorage.getItem(TEACHER_AUTH_KEY) === "1";
  } catch {
    return false;
  }
}

export function setTeacherUnlocked(unlocked: boolean): void {
  try {
    if (unlocked) localStorage.setItem(TEACHER_AUTH_KEY, "1");
    else localStorage.removeItem(TEACHER_AUTH_KEY);
  } catch { /* ignore */ }
}

export function listCustomLessons(): CustomLesson[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as CustomLesson[];
    if (!Array.isArray(parsed)) return [];
    return parsed.sort((a, b) => b.updatedAt - a.updatedAt);
  } catch {
    return [];
  }
}

export function getCustomLesson(id: string): CustomLesson | null {
  return listCustomLessons().find((l) => l.id === id) ?? null;
}

export function saveCustomLesson(lesson: CustomLesson): CustomLesson {
  const all = listCustomLessons();
  const idx = all.findIndex((l) => l.id === lesson.id);
  const now = Date.now();
  const next: CustomLesson = {
    ...lesson,
    isCustom: true,
    updatedAt: now,
    createdAt: idx >= 0 ? all[idx]!.createdAt : now,
  };
  if (idx >= 0) all[idx] = next;
  else all.push(next);
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
  } catch { /* quota — swallow */ }
  return next;
}

export function deleteCustomLesson(id: string): void {
  const all = listCustomLessons().filter((l) => l.id !== id);
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
  } catch { /* ignore */ }
}

export function newLessonId(): string {
  return "l_custom_" + Math.random().toString(36).slice(2, 10);
}

/**
 * Build an empty lesson with sensible defaults. Order index sits AFTER any
 * existing custom lessons so it appears at the bottom of the play list.
 */
export function blankLesson(): CustomLesson {
  const all = listCustomLessons();
  const nextOrder = 1000 + all.length;
  return {
    id: newLessonId(),
    title: "New Lesson",
    theme: "candy",
    timeOfDay: "auto",
    questionsCount: 5,
    objectsCount: 10,
    coinsCount: 30,
    isGlobal: false,
    isCustom: true,
    orderIndex: nextOrder,
    challengeKinds: null,
    words: [],
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
}

export function newWord(): WordDTO {
  const id = "w_custom_" + Math.random().toString(36).slice(2, 8);
  return {
    id,
    english: "",
    vietnamese: "",
    sentence: "",
    imageUrl: null,
    audioUrl: null,
    sentenceAudioUrl: null,
    orderIndex: 0,
  };
}

export const TEACHER_DEFAULT_PASSCODE = "teach";

export const THEME_OPTIONS: ThemeId[] = ["candy", "forest", "jungle", "snow", "desert", "space"];
export const TIME_OPTIONS: TimeOfDay[] = ["auto", "morning", "afternoon", "sunset", "night"];

/**
 * One-shot seeder: if the teacher has never opened the workbook
 * before, clone every built-in WORD_PACK as a custom lesson so they
 * can SEE the structure + edit each pack as a starting point.
 * Idempotent — only runs on truly empty state (or after the user
 * clears localStorage).
 */
const SEED_KEY = "wr_teacher_seeded_v1";
export function ensureBuiltInsSeeded(packs: Array<{
  id: string;
  title: string;
  theme: ThemeId;
  timeOfDay: TimeOfDay;
  words: WordDTO[];
  orderIndex: number;
}>): void {
  try {
    if (localStorage.getItem(SEED_KEY) === "1") return;
    const existing = listCustomLessons();
    // Only seed on a fully-empty workbook — don't overwrite the teacher's
    // own packs if they cleared the flag for any reason.
    if (existing.length > 0) {
      localStorage.setItem(SEED_KEY, "1");
      return;
    }
    for (const p of packs) {
      const clonedWords: WordDTO[] = p.words.map((w, i) => ({
        ...w,
        // Give each word a fresh id so a re-import / edit doesn't
        // overwrite the original built-in word.
        id: `w_seed_${p.id.replace(/^pack_/, "")}_${i}`,
        orderIndex: i,
      }));
      saveCustomLesson({
        id: `l_seed_${p.id.replace(/^pack_/, "")}`,
        title: p.title,
        theme: p.theme,
        timeOfDay: p.timeOfDay,
        questionsCount: Math.min(5, clonedWords.length || 1),
        objectsCount: 10,
        coinsCount: 30,
        isGlobal: false,
        isCustom: true,
        orderIndex: 100 + p.orderIndex,
        challengeKinds: null,
        words: clonedWords,
        createdAt: Date.now(),
        updatedAt: Date.now(),
      });
    }
    localStorage.setItem(SEED_KEY, "1");
  } catch {
    /* localStorage failures — ignore, teacher can still use the
     * workbook, just won't be auto-seeded. */
  }
}
